import * as THREE from 'three';
import { litMaterial } from '../core/materials';
import { buildModel } from '../entities/models';
import type { World } from '../game/World';
import { uploadFirst } from '../utils/gpu';

export type ItemKind = 'coin' | 'food' | 'magnet' | 'bomb' | 'freeze' | 'chest';

interface Gem {
  active: boolean;
  x: number;
  z: number;
  value: number;
  pulled: boolean;
  speed: number;
  phase: number;
}

interface Item {
  active: boolean;
  kind: ItemKind;
  x: number;
  z: number;
  pulled: boolean;
  speed: number;
  phase: number;
}

const GEM_CAP = 700;
const ITEM_CAP = 24;
const TIERS: Array<[number, string, number]> = [
  [2, '#4fc3ff', 0.34],
  [5, '#5dff8a', 0.4],
  [15, '#ff5d6d', 0.46],
  [40, '#c77dff', 0.54],
  [Infinity, '#ffd24d', 0.64],
];

const ITEM_PARTS: Record<ItemKind, Parameters<typeof buildModel>[0]> = {
  coin: [{ s: 'cyl', d: [0.45, 0.08, 0.45], p: [0, 0.5, 0], r: [Math.PI / 2, 0, 0], c: '#ffcc33' }, { s: 'cyl', d: [0.3, 0.1, 0.3], p: [0, 0.5, 0], r: [Math.PI / 2, 0, 0], c: 'G' }],
  food: [{ s: 'sphere', d: [0.55, 0.5, 0.55], p: [0, 0.45, 0], c: '#e0313b' }, { s: 'cone', d: [0.12, 0.25, 0.12], p: [0.06, 0.78, 0], r: [0, 0, -0.5], c: '#4caf50' }, { s: 'sphere', d: [0.62, 0.56, 0.62], p: [0, 0.45, 0], c: 'G' }],
  magnet: [{ s: 'torus', d: [0.8, 0.8, 1.2], p: [0, 0.6, 0], c: '#d23b3b' }, { s: 'box', d: [0.14, 0.18, 0.18], p: [0.32, 0.38, 0], c: '#dfe6ee' }, { s: 'box', d: [0.14, 0.18, 0.18], p: [-0.32, 0.38, 0], c: '#dfe6ee' }, { s: 'ico', d: [0.25, 0.25, 0.25], p: [0, 0.95, 0], c: 'G' }],
  bomb: [{ s: 'sphere', d: [0.55, 0.55, 0.55], p: [0, 0.42, 0], c: '#2b2b38' }, { s: 'cyl', d: [0.06, 0.2, 0.06], p: [0, 0.78, 0], c: '#8b6b4a' }, { s: 'ico', d: [0.18, 0.18, 0.18], p: [0, 0.92, 0], c: 'G' }],
  freeze: [{ s: 'oct', d: [0.4, 0.75, 0.4], p: [0, 0.6, 0], c: 'G' }, { s: 'cyl', d: [0.5, 0.08, 0.5], p: [0, 0.18, 0], c: '#9fc7e6' }],
  chest: [
    { s: 'box', d: [1.0, 0.55, 0.7], p: [0, 0.28, 0], c: '#7a4a24' },
    { s: 'box', d: [1.04, 0.28, 0.74], p: [0, 0.68, 0], c: '#8e5a2e' },
    { s: 'box', d: [1.06, 0.08, 0.1], p: [0, 0.55, 0.36], c: '#ffd24d' },
    { s: 'box', d: [0.16, 0.2, 0.06], p: [0, 0.5, 0.38], c: 'G' },
    { s: 'cyl', d: [0.5, 9, 0.5], p: [0, 4.5, 0], c: 'G' },
  ],
};

const ITEM_GLOW: Record<ItemKind, string> = {
  coin: '#ffe066', food: '#ff7a7a', magnet: '#8fb8ff', bomb: '#ff9a3c', freeze: '#9ff3ff', chest: '#ffd24d',
};

export class Pickups {
  readonly root = new THREE.Group();
  private readonly gems: Gem[] = [];
  private readonly items: Item[] = [];
  private readonly gemMesh: THREE.InstancedMesh;
  private readonly itemMeshes = new Map<ItemKind, { body: THREE.InstancedMesh; glow: THREE.InstancedMesh | null; n: number }>();
  private mergeCursor = 0;
  private activeGemCount = 0;
  private readonly matrix = new THREE.Matrix4();
  private readonly quat = new THREE.Quaternion();
  private readonly posV = new THREE.Vector3();
  private readonly scaleV = new THREE.Vector3();
  private readonly color = new THREE.Color();
  private readonly up = new THREE.Vector3(0, 1, 0);
  private readonly tierColors = TIERS.map(([, c]) => new THREE.Color(c));

  constructor(
    private readonly world: World,
    private readonly onGem: (value: number) => void,
    private readonly onItem: (kind: ItemKind) => void,
  ) {
    for (let i = 0; i < GEM_CAP; i += 1) this.gems.push({ active: false, x: 0, z: 0, value: 0, pulled: false, speed: 0, phase: 0 });
    for (let i = 0; i < ITEM_CAP; i += 1) this.items.push({ active: false, kind: 'coin', x: 0, z: 0, pulled: false, speed: 0, phase: 0 });
    this.gemMesh = new THREE.InstancedMesh(new THREE.OctahedronGeometry(0.5, 0).scale(0.8, 1.2, 0.8), new THREE.MeshBasicMaterial({ color: '#ffffff' }), GEM_CAP);
    this.gemMesh.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(GEM_CAP * 3), 3);
    this.gemMesh.frustumCulled = false;
    this.gemMesh.count = 0;
    this.gemMesh.visible = false;
    this.root.add(this.gemMesh);
    for (const kind of Object.keys(ITEM_PARTS) as ItemKind[]) {
      const model = buildModel(ITEM_PARTS[kind], '#ffffff');
      const body = new THREE.InstancedMesh(model.body, litMaterial({ vertexColors: true, flatShading: true, roughness: 0.4, metalness: 0.3 }), 8);
      body.castShadow = kind === 'chest';
      body.frustumCulled = false;
      body.count = 0;
      body.visible = false;
      this.root.add(body);
      let glow: THREE.InstancedMesh | null = null;
      if (model.glow) {
        const transparent = kind === 'chest' || kind === 'food';
        glow = new THREE.InstancedMesh(
          model.glow,
          new THREE.MeshBasicMaterial({
            color: new THREE.Color(ITEM_GLOW[kind]).multiplyScalar(transparent ? 0.45 : 1.9),
            transparent,
            opacity: transparent ? 0.18 : 1,
            blending: transparent ? THREE.AdditiveBlending : THREE.NormalBlending,
            depthWrite: !transparent,
          }),
          8,
        );
        glow.frustumCulled = false;
        glow.count = 0;
        glow.visible = false;
        this.root.add(glow);
      }
      this.itemMeshes.set(kind, { body, glow, n: 0 });
    }
  }

  clear(): void {
    for (const g of this.gems) g.active = false;
    for (const it of this.items) it.active = false;
    this.render(0);
  }

  get gemCount(): number {
    return this.activeGemCount;
  }

  dropGem(x: number, z: number, value: number): void {
    if (value <= 0) return;
    const gem = this.gems.find((g) => !g.active);
    if (gem) {
      gem.active = true;
      gem.x = x;
      gem.z = z;
      gem.value = value;
      gem.pulled = false;
      gem.speed = 0;
      gem.phase = this.world.rng() * 6;
      return;
    }
    // Pool full: fold the value into an existing gem so XP is never lost.
    this.mergeCursor = (this.mergeCursor + 1) % GEM_CAP;
    this.gems[this.mergeCursor].value += value;
  }

  dropItem(kind: ItemKind, x: number, z: number): void {
    let it = this.items.find((i) => !i.active);
    if (!it && kind === 'chest') {
      // Pool full of left-behind coins and food: a chest takes the slot of the one farthest from the player.
      const { x: px, z: pz } = this.world.player;
      const dist = (i: { x: number; z: number }) => (i.x - px) ** 2 + (i.z - pz) ** 2;
      for (const i of this.items) if (i.kind !== 'chest' && (!it || dist(i) > dist(it))) it = i;
    }
    if (!it) return;
    it.active = true;
    it.kind = kind;
    it.x = x;
    it.z = z;
    it.pulled = false;
    it.speed = 0;
    it.phase = this.world.rng() * 6;
  }

  // Closest unclaimed gem/item (used by the test autopilot to play like a human).
  nearest(x: number, z: number, maxDist: number): { x: number; z: number } | null {
    let best: { x: number; z: number } | null = null;
    let bestD = maxDist * maxDist;
    for (const list of [this.gems, this.items]) for (const g of list) {
      if (!g.active || g.pulled) continue;
      const d = (g.x - x) ** 2 + (g.z - z) ** 2;
      if (d < bestD) {
        bestD = d;
        best = g;
      }
    }
    return best;
  }

  magnetRadiusAt(x: number, z: number, r: number): void {
    const r2 = r * r;
    for (const g of this.gems) {
      if (g.active && (g.x - x) ** 2 + (g.z - z) ** 2 <= r2) g.pulled = true;
    }
  }

  magnetAll(): void {
    for (const g of this.gems) if (g.active) g.pulled = true;
  }

  update(dt: number, magnetRadius: number): void {
    const w = this.world;
    const px = w.player.x;
    const pz = w.player.z;
    const mr2 = magnetRadius * magnetRadius;
    for (const g of this.gems) {
      if (!g.active) continue;
      const dx = px - g.x;
      const dz = pz - g.z;
      const d2 = dx * dx + dz * dz;
      if (!g.pulled && d2 < mr2) {
        g.pulled = true;
        g.speed = -3;
      }
      if (g.pulled) {
        g.speed = Math.min(26, g.speed + 40 * dt);
        const d = Math.sqrt(d2) || 1;
        const step = Math.min(d, g.speed * dt);
        g.x += (dx / d) * step;
        g.z += (dz / d) * step;
        if (d < 0.55) {
          g.active = false;
          this.onGem(g.value);
        }
      }
    }
    for (const it of this.items) {
      if (!it.active) continue;
      const dx = px - it.x;
      const dz = pz - it.z;
      const d = Math.hypot(dx, dz);
      const reach = it.kind === 'chest' ? 1.2 : magnetRadius * 0.8;
      if (!it.pulled && d < reach) {
        it.pulled = true;
        it.speed = it.kind === 'chest' ? 30 : -2;
      }
      if (it.pulled) {
        it.speed = Math.min(22, it.speed + 30 * dt);
        const step = Math.min(d, Math.max(0, it.speed) * dt);
        if (d > 0) {
          it.x += (dx / d) * step;
          it.z += (dz / d) * step;
        }
        if (d < 0.6) {
          it.active = false;
          this.onItem(it.kind);
        }
      }
    }
    this.render(w.time);
  }

  // Positions of chests for the off-screen markers.
  chests(each: (x: number, z: number) => void): void {
    for (const it of this.items) if (it.active && it.kind === 'chest') each(it.x, it.z);
  }

  private render(time: number): void {
    let n = 0;
    let activeGems = 0;
    const px = this.world.player.x;
    const pz = this.world.player.z;
    for (const g of this.gems) {
      if (!g.active) continue;
      activeGems += 1;
      if (Math.abs(g.x - px) > 26 || Math.abs(g.z - pz) > 22) continue;
      let tier = 0;
      while (g.value >= TIERS[tier][0]) tier += 1;
      const size = TIERS[tier][2];
      this.posV.set(g.x, 0.35 + Math.sin(time * 4 + g.phase) * 0.08, g.z);
      this.quat.setFromAxisAngle(this.up, time * 2 + g.phase);
      this.scaleV.setScalar(size);
      this.matrix.compose(this.posV, this.quat, this.scaleV);
      this.gemMesh.setMatrixAt(n, this.matrix);
      this.gemMesh.setColorAt(n, this.color.copy(this.tierColors[tier]).multiplyScalar(1.3));
      n += 1;
    }
    this.activeGemCount = activeGems;
    this.gemMesh.count = n;
    this.gemMesh.visible = n > 0;
    uploadFirst(this.gemMesh.instanceMatrix, n);
    uploadFirst(this.gemMesh.instanceColor, n);

    for (const m of this.itemMeshes.values()) m.n = 0;
    for (const it of this.items) {
      if (!it.active) continue;
      if (Math.abs(it.x - px) > 26 || Math.abs(it.z - pz) > 22) continue;
      const m = this.itemMeshes.get(it.kind)!;
      if (m.n >= 8) continue;
      const bob = it.kind === 'chest' ? 0 : 0.15 + Math.sin(time * 3 + it.phase) * 0.12;
      this.posV.set(it.x, bob, it.z);
      this.quat.setFromAxisAngle(this.up, it.kind === 'chest' ? 0.4 : time * 1.8 + it.phase);
      this.scaleV.setScalar(it.kind === 'chest' ? 1 : 1.1);
      this.matrix.compose(this.posV, this.quat, this.scaleV);
      m.body.setMatrixAt(m.n, this.matrix);
      m.glow?.setMatrixAt(m.n, this.matrix);
      m.n += 1;
    }
    for (const m of this.itemMeshes.values()) {
      m.body.count = m.n;
      m.body.visible = m.n > 0;
      uploadFirst(m.body.instanceMatrix, m.n);
      if (m.glow) {
        m.glow.count = m.n;
        m.glow.visible = m.n > 0;
        uploadFirst(m.glow.instanceMatrix, m.n);
      }
    }
  }
}
